import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { ApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { FormError } from '@/components/ui/FormLayout'
import { InfoBanner } from '@/components/ui/InfoBanner'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { TextField } from '@/components/ui/TextField'
import { useToast } from '@/components/ui/Toast'
import { CheckIcon, InfoIcon, UserPlusIcon } from '@/components/ui/icons'
import { useAuth } from '@/features/auth/authContext'
import { useCreateInvitation } from '@/features/invitations/hooks/useInvitationActions'
import { useInvitationRoles } from '@/features/invitations/hooks/useInvitationRoles'
import { invitationLink } from '@/features/invitations/invitationLink'
import { type InviteInput, inviteSchema } from '@/features/invitations/schemas/invitationSchemas'
import type { InvitationCreated } from '@/features/invitations/types'

import form from '@/components/ui/FormLayout/formLayout.module.css'

const FORM_ID = 'invite-user-form'

const EMPTY: InviteInput = { email: '', fullName: '', role: '' }

/** Request field names that match a field on this form. */
const FORM_FIELDS = new Set<keyof InviteInput>(['email', 'fullName', 'role'])

interface InviteUserModalProps {
  open: boolean
  onClose: () => void
}

export function InviteUserModal({ open, onClose }: InviteUserModalProps) {
  const { location } = useAuth()
  const roles = useInvitationRoles()
  const createInvitation = useCreateInvitation()
  const { showToast } = useToast()
  const [created, setCreated] = useState<InvitationCreated | null>(null)

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteInput>({ resolver: zodResolver(inviteSchema), defaultValues: EMPTY })

  // Closing forgets the link and the form, so the next opening starts clean.
  const close = () => {
    reset(EMPTY)
    setCreated(null)
    onClose()
  }

  const submit = handleSubmit(async (values) => {
    if (!location) {
      setError('root', { message: 'Bitte wählen Sie zuerst einen Standort.' })
      return
    }
    try {
      setCreated(
        await createInvitation.mutateAsync({ input: values, locationPublicId: location.publicId }),
      )
    } catch (error) {
      const apiError = ApiError.from(error)

      for (const field of Object.keys(apiError.fieldErrors)) {
        if (FORM_FIELDS.has(field as keyof InviteInput)) {
          setError(field as keyof InviteInput, { message: 'Bitte prüfen Sie dieses Feld.' })
        }
      }

      if (apiError.code === 'EMAIL_TAKEN') {
        setError('email', { message: 'Für diese E-Mail-Adresse besteht bereits ein Konto.' })
      }

      setError('root', {
        message:
          apiError.code === 'INVITATION_PENDING'
            ? 'Diese Einladung wird gerade erstellt. Bitte senden Sie sie nicht erneut.'
            : apiError.message,
      })
    }
  })

  const link = created ? invitationLink(created.token) : ''

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link)
      showToast({ title: 'Link kopiert' })
    } catch {
      // No clipboard outside a secure context; the field stays selectable instead.
      showToast({
        title: 'Kopieren nicht möglich',
        description: 'Bitte markieren und kopieren Sie den Link manuell.',
        tone: 'error',
      })
    }
  }

  const expiresAt = created
    ? new Intl.DateTimeFormat('de-DE', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: location?.timezone,
      }).format(new Date(created.expiresAt))
    : ''

  return (
    <Modal
      open={open}
      onClose={close}
      title="Mitarbeiter einladen"
      subtitle={location ? `Für ${location.name}` : 'Für Ihren aktuellen Standort'}
      size="sm"
      footer={
        created ? (
          <>
            <Button variant="ghost" onClick={close}>
              Fertig
            </Button>
            <Button leadingIcon={<CheckIcon />} onClick={() => void copy()}>
              Link kopieren
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              isLoading={isSubmitting}
              leadingIcon={<UserPlusIcon />}
            >
              Einladung erstellen
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className={form.grid}>
          <InfoBanner className={form.full} icon={<InfoIcon />}>
            Der Link wird nur jetzt angezeigt. Kopieren Sie ihn und geben Sie ihn an{' '}
            {created.fullName} weiter. Gültig bis {expiresAt}.
          </InfoBanner>

          <TextField
            className={form.full}
            label="Einladungslink"
            value={link}
            readOnly
            onFocus={(event) => event.currentTarget.select()}
          />
        </div>
      ) : (
        <form id={FORM_ID} className={form.grid} onSubmit={submit} noValidate>
          <FormError message={errors.root?.message} />

          <TextField
            className={form.full}
            label="Name"
            placeholder="Vor- und Nachname"
            autoComplete="off"
            error={errors.fullName?.message}
            {...register('fullName')}
          />

          <TextField
            className={form.full}
            label="E-Mail"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            error={errors.email?.message}
            {...register('email')}
          />

          <Select
            className={form.full}
            label="Rolle"
            disabled={!roles.data}
            error={roles.isError ? 'Rollen konnten nicht geladen werden.' : errors.role?.message}
            {...register('role')}
          >
            <option value="">{roles.isPending ? 'Wird geladen …' : 'Bitte wählen'}</option>
            {roles.data?.map((role) => (
              <option key={role.key} value={role.key}>
                {role.label}
              </option>
            ))}
          </Select>
        </form>
      )}
    </Modal>
  )
}
